<template>
	<div class="w-full">
    
		<!-- IMAGE EDITOR -->
		<div class="fullSizeEditor">
		
			<div class="grid grid-cols-12 h-full bg-gray-500" v-if="layout.action.edit">
				
				<!-- LEFT PANEL -->
				<div id="editorLeftPanel" class="col-span-9 xl:col-span-9 lg:col-span-8 md:col-span-8">

					<!-- IMAGE METADATA -->
					<template v-if="!layout.canvas.loading">
						<div id="metadata-bar" class="editorTopTools">
							<div class="editorTopTool">
								<span v-if="layout.canvas.drawing">{{ $t('Drawing mode') }}</span>
								<span v-else>{{ $t('Position mode') }}</span>
							</div>
							<div class="editorTopTool">
								{{ layout.action.edit?.createdDate || '' }}
							</div>
							<div class="editorTopTool">
								<ImageIcon class="w-4 h-4 mr-1" />
								{{ layout.action.edit.gimageObj?.meta?.size ? (layout.action.edit.gimageObj.meta.size / 1024).toFixed(0) : '0' }} KB
							</div>
							<div class="editorTopTool">
								{{ layout.action.edit?.name ? layout.action.edit.name.split('/').pop() : '' }}
							</div>
                            </div>
					</template>

					<!-- MAIN CANVAS -->
					<div id="canvasBox" class="w-full h-full bg-gray-500" style="overflow: hidden !important">

						<div v-if="layout.canvas.error" class="text-center mt-60 text-gray-700">
							<div class="mb-2">
								<Alert-circleIcon class="w-20 h-20 text-gray-700" />
							</div>
							{{ $t('Failed to load image') }}
						</div>

						<div v-else>

							<div class="flex items-center justify-center w-full h-full"
								:class="layout.canvas.loading ? '' : 'hidden'"
								:style="`background-image: url(${require(`@/assets/images/rosepetal/icon/loading-image.png`)}); background-position: center; background-size: cover; background-repeat: no-repeat; height: 100%; height: 100vh;`">
							</div>

							<div :class="layout.canvas.loading ? 'hidden' : ''"
								:style="`background-image: url(${require(`@/assets/images/rosepetal/icon/back-editor.png`)}); background-position: top center; background-size: cover; background-repeat: no-repeat; height: 100%; min-height: 100vh;`">
								<div id="canvasContent" class="w-full h-full">
									<canvas id="c"></canvas>
								</div>
							</div>

						</div>

						<div id="save-status-bar" v-if="autoSaveStatus" class="save-status-indicator" :class="{'is-saving': autoSaveStatus.includes('Saving')}">
							<div class="save-status-content">
								<template v-if="autoSaveStatus.includes('Saving')">
									<svg class="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
										<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
										<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
									</svg>
                                </template>
                                <template v-else>
                                    <template v-if="autoSaveStatus === 'Save failed'">
                                        <XIcon class="w-4 h-4 mr-2" />
								</template>
								<template v-else>
									<CheckIcon class="w-4 h-4 mr-2" />
                                    </template>
								</template>
								{{ autoSaveStatus }}
							</div>
						</div>

						<EditorToolbarCmp
                            ref="editorToolbarRef"
							:layout="layout"
							:tagCounts="tagCounts"
                            :enableSAM="enableSAM"
                            :enableVLM="enableVLM"
                            :processing="vlmProcessing"
                            :VLMActive="VLMActive"
                            :newImage="autoNewImage"
                            :stepTimes="stepTimes"
                            :samSteps="samSteps"
                            :SAMActive="SAMActive"
                            @setupTool="setupTool($event)"
                            @tagSelected="setupTool(layout.canvas.tools.currentTool)"
							@updateBrushSize="handleBrushSize"
                            @setPredictedMask="setPredictedMask"
                            @updateCanvas="updateSelectedCanvas($event, layout.selectedCanvas)"
                            @openCreateTag="showCreateTagModal = true"
                            @singleImage-VLM-Infere="handleVLMInference"
                            @next-image="nextImage"
                            @VLMnoActive="deactivateVLM"
                            @showSamHelp="showSamIntroPopup = true"/>

                        <EditorFiltersCmp
                            :layout="layout"
                            :canvas="canvasboxRef"
                            :currentBackground="currentBackground"
                            :currentObjects="currentObjects"
							@resetZoom="resetZoom"
							@handleZoom="handleZoom"
                            @updateFilters="layout.canvas.filters = $event"
                            @updateOpacity="layout.canvas.filters.opacity = $event"/>

					</div>

				</div>

				<!-- RIGHT PANEL -->
				<div id="editorRightPanel" class="col-span-3 xl:col-span-3 lg:col-span-4 md:col-span-4">
					<div class="h-full overflow-auto bg-white">
						
						<!-- TOOLS BAR -->
						<div class="bg-gray-50 border-b border-gray-200 sticky top-0 z-20">

							<div class="grid grid-cols-4 divide-x divide-gray-200">

								<button title="Download"
									type="button"
									class="action-button"
									v-if="!layout.canvas.error"
									@click="showDownloadModal = true">
									<DownloadIcon class="action-icon" />
								</button>

								<button title="Copy"
									type="button"
									class="action-button"
									v-if="!layout.canvas.error"
									@click="showCopyModal = true">
									<CopyIcon class="action-icon" />
								</button>

								<button title="Delete" 
                                    type="button" 
                                    class="action-button action-button-danger" 
                                    v-if="!layout.canvas.error" 
                                    @click="showDeleteModal = true">
                                    <Trash2Icon class="action-icon" />
                                </button>

								<button title="Exit"
									type="button"
									class="action-button"
									data-dismiss="modal"
									@click="exitEditor()">
									<Log-outIcon class="action-icon" />
								</button>

							</div>

							<!-- PAGINATION CONTROLS -->
							<div v-if="layout.action.edit" class="border-t border-gray-200">
								<NavigatorCmp
                                    ref="navigatorRef"
									:dataset="layout.dataset"
									:image="layout.action.edit"
									:filters="layout.navFilters"
									:currentPage="layout.currentPage"
									:hasChanges="hasChangesToSave"
									@editImage="editImage($event)"
									@saveImage="saveImage" />
							</div>

						</div>

						<!-- RIGHT PANEL MENU -->
						<div class="rpEditorMenu">
							<div v-if="!layout.canvas.loading">
								<ul class="divide-y divide-gray-200">

									<!-- FILTERS SECTION -->
									<li class="nav flex items-center">
										<span class="text-sm text-gray-700 mr-2">{{ $t('Filters') }}:</span>
										<div class="flex flex-row space-x-2 flex-1 ml-8 mt-1 mb-1">

											<select v-if="layout.dataset && layout.dataset.type === 'imageObjectDetection'"
												v-model="layout.navFilters.objtagsType"
												class="custom-select text-xs">
												<option value="all">{{ $t('All') }}</option>
												<option value="labeled" v-if="Object.keys(filteredTags).length">{{ $t('Labeled') }}</option>
												<option value="notLabeled">{{ $t('Not labeled') }}</option>
											</select>

											<select v-if="layout.dataset && layout.dataset.type === 'MULTILABEL'"
												v-model="layout.navFilters.objtagsType"
												class="custom-select text-xs">
												<option value="all">{{ $t('All') }}</option>
												<option value="anomaly" v-if="Object.keys(filteredTags).length">{{ $t('Anomaly') }}</option>
												<option value="normal">{{ $t('Normal') }}</option>
											</select>

											<select v-if="showTags"
												v-model="layout.navFilters.objByTag"
												class="custom-select text-xs">
												<option value="all">{{ $t('All tags') }}</option>
												<option v-for="(tag) in filteredTags" :value="tag.id" :key="tag.id">{{ tag.name }}</option>
											</select>

											<select v-model="layout.navFilters.order" class="custom-select text-xs">
												<option value="date">{{ $t('Created') }}</option>
												<option value="updatedAt">{{ $t('Updated') }}</option>
											</select>

											<select v-model="layout.navFilters.direction" class="custom-select text-xs">
												<option value="asc">{{ $t('Asc') }}</option>
												<option value="desc">{{ $t('Desc') }}</option>
											</select>

										</div>
									</li>

									<!-- IMAGE TYPE SECTION -->
									<li class="nav flex items-center" v-if="layout.dataset.type === 'MULTILABEL'">
										<span class="text-sm text-gray-700 mr-10">{{ $t('Type') }}:</span>
										<div class="image-type-toggle flex-1">
											<div class="toggle-container">
												<input type="checkbox"
													id="imageTypeToggle"
													class="toggle-input"
                                                    :checked="layout.status.multilabelTag?.id == 'OK'"
													:disabled="!normalButtonEnabled"
													@change="handleToggleChange">
												<label for="imageTypeToggle" class="toggle-switch">
													<span class="toggle-option anomaly">{{ $t('Anomaly') }}</span>
													<span class="toggle-option normal">{{ $t('Normal') }}</span>
													<span class="toggle-slider"></span>
												</label>
											</div>
										</div>
									</li>

									<!-- LABELING SECTION -->
									<template v-if="showLabeling">
										
                                        <li id="eMlabeling" class="nav selected" @click="toggleMenuSection('eMlabeling')">
											<span class="text-sm text-gray-700">{{ $t('Labeling') }}</span>
											<span class="arrow-icon"></span>
										</li>
										<li id="eMtags" class="menu-content eMlabeling sel" style="padding: 0 2rem">

											<div v-if="layout.dataset.type !== 'ANOMALY'" class="sticky-top-controls" style="margin-bottom: 0.5rem">
                                                
                                                <div class="flex justify-between gap-2 pt-4 pb-1">
      
                                                    <template v-if="layout.dataset.type !== 'MULTICLASS'">
                                                        <button type="button"
                                                            class="rp-btn-action-danger"
                                                            @click="layout.dataset.type === 'MULTILABEL' ? hideMasks(true) : hideBoxes(true)">
                                                            <TrashIcon class="w-4 h-4 mr-1" />
                                                            {{ $t('Remove all') }}
                                                        </button>

                                                        <button type="button"
                                                            class="rp-btn-action-secondary"
                                                            @click="
                                                                layout.dataset.type === 'MULTILABEL'
                                                                ? (layout.canvas.showMasks = !layout.canvas.showMasks, hideMasks())
                                                                : (layout.canvas.showBoxes = !layout.canvas.showBoxes, hideBoxes())
                                                            "
                                                            >
                                                            <component
                                                                :is="(layout.dataset.type === 'MULTILABEL' ? layout.canvas.showMasks : layout.canvas.showBoxes)
                                                                ? 'Eye-offIcon'
                                                                : 'EyeIcon'"
                                                                class="w-4 h-4 mr-1"
                                                            />
                                                            {{
                                                                (layout.dataset.type === 'MULTILABEL' ? layout.canvas.showMasks : layout.canvas.showBoxes)
                                                                ? $t('Hide all')
                                                                : $t('Show all')
                                                            }}
                                                        </button>
                                                    </template>

                                                    <button  type="button" class="rp-btn-action-primary" @click="showCreateTagModal = true">
                                                        <PlusIcon class="w-4 h-4 mr-1" />
                                                        {{ $t('New tag type') }}
                                                    </button>

                                                </div>

                                                <div v-if="layout.dataset.type !== 'MULTICLASS'" class="flex items-center">
                                                    <span class="text-sm text-gray-700 font-medium pt-3 pb-1">
                                                        {{ layout.dataset.type === 'MULTILABEL' ? $t('Masks') : $t('Bounding boxes') }}
                                                    </span>
                                                </div>

											</div>

                                            <!-- CLASSIFICATION LIST -->
                                            <div v-if="layout.dataset.type === 'MULTICLASS' || layout.dataset.type === 'ANOMALY'" class="space-y-1 mt-1" style="padding-bottom: 1rem;">
                                                <div v-if="!Object.keys(layout.dataset.tags).length" class="p-4 rounded-lg bg-gray-50 border border-gray-200">
                                                    <div class="text-sm font-medium text-gray-800 mb-1">{{ $t('Image not labeled') }}</div>
                                                    <div class="text-xs text-gray-600">{{ $t('Must add tags to the dataset to start labeling') }}</div>
                                                </div>
                                                
                                                                                <div v-else v-for="(tag, index) in Object.values(layout.dataset.tags)" :key="tag.id"
                                    :id="`tag-card-${tag.id}`"
                                    class="group flex items-center p-2 rounded-lg border border-gray-200 hover:border-gray-300 transition-all cursor-pointer"
                                    :class="layout.status.currentTag && layout.status.currentTag.id == tag.id ? 'bg-gray-50 border-gray-300 shadow-sm' : 'bg-white'"
                                    @click="layout.status.currentTag = tag; saveImage(true, true)">

                                    <div class="ml-1 relative flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center shadow-inner"
                                        :style="'background-color: ' + (tag.color || '#6b7280')">
                                        <EyeIcon class="w-3 h-3 text-white opacity-80" />
                                    </div>

                                    <div class="flex-1 mx-2">
                                        <span class="text-sm text-gray-900">{{ tag.name }}</span>
                                    </div>

                                    <div class="flex-shrink-0 flex items-center gap-1">
                                        <div v-if="layout.status.currentTag && layout.status.currentTag.id == tag.id" 
                                            class="p-1 text-green-500">
                                            <CheckIcon class="w-4 h-4" />
                                        </div>
                                        <kbd class="key-indicator">{{ index + 1 }}</kbd>
                                    </div>
                                </div>
											</div>

											<!-- MASKS LIST -->
											<div v-if="layout.dataset.type === 'MULTILABEL'" class="space-y-1 mt-1" style="padding-bottom: 1rem;">
												<div v-if="!canvasIds.length" class="p-4 rounded-lg bg-gray-50 border border-gray-200">
													<div class="text-sm font-medium text-gray-800 mb-1">{{ $t('Image not labeled with masks')}}</div>
													<div class="text-xs text-gray-600">{{ $t('Select the pencil tool and one tag for draw') }}.</div>
												</div>

												<div v-else v-for="canvas in canvasIds" :key="canvas.uuid"
													:id="`box-card-${canvas.uuid}`"
													class="group flex items-center p-2 rounded-lg border border-gray-200 hover:border-gray-300 transition-all cursor-pointer"
													:class="layout.selectedCanvas == canvas ? 'bg-gray-50 border-gray-300 shadow-sm' : 'bg-white'"
													@click.stop="layout.selectedCanvas == canvas ? layout.selectedCanvas = null : layout.selectedCanvas = canvas; setupTool('brush');"
														 @mouseover="highlightElement(canvas.uuid)" 
														 @mouseleave="highlightElement(canvas.uuid, true)">

													<div class="ml-1 relative flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center shadow-inner"
                                                        :style="'background-color: ' + canvas.stroke">
														<EyeIcon class="w-3 h-3 text-white opacity-80" />
													</div>

													<select class="flex-1 custom-select mx-2"
															v-model="canvas.name"
															@click.stop
															@change="updateSelectedCanvas($event, canvas.uuid)">
														<option v-for="(tag, indx) in filteredTags" 
																:value="indx"
																:key="indx">
															{{ tag.name }}
														</option>
													</select>

													<button class="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 transition-colors hover:bg-gray-50 rounded" 
															@click.stop="removeElement(canvas.uuid)">
														<Trash2Icon class="w-5 h-5 mx-auto"/>
													</button>
												</div>
											</div>

											<!-- BOUNDING BOXES LIST -->
											<div id="bounding-boxes-list" v-if="layout.dataset.type === 'imageObjectDetection'" class="space-y-1 mt-1" style="padding-bottom: 1rem;">

												<div v-if="!currentObjects.filter(obj => obj.elementType === 'bbRect').length" class="p-4 rounded-lg bg-gray-50 border border-gray-200">
													<div class="text-sm font-medium text-gray-800 mb-1">{{ $t('Image not labeled')}}</div>
													<div class="text-xs text-gray-600">
														{{ $t('Add bounding boxes and labels')}}.
														<br />
														{{ $t('Use the mouse to draw a new box on the image') }}.
													</div>
												</div>

												<div v-else v-for="box in currentObjects.filter(obj => obj.elementType === 'bbRect' && obj.name)" 
													 :key="box.uuid"
													 :id="`box-card-${box.uuid}`"
													 class="group flex items-center p-2 rounded-lg border border-gray-200 hover:border-gray-300 transition-all cursor-pointer"
													 :class="layout.selectedBB === box.uuid ? 'bg-gray-50 border-gray-300 shadow-sm' : 'bg-white'"
                                                    @click="selectBoundingBox(box.uuid)"
														 @mouseover="highlightElement(box.uuid)" 
														 @mouseleave="highlightElement(box.uuid, true)">

													<div class="ml-1 relative flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center shadow-inner"
                                                        :style="'background-color: ' + box.stroke">
														<EyeIcon class="w-3 h-3 text-white opacity-80" />
													</div>

													<select class="flex-1 custom-select mx-2"
                                                        v-model="box.id"
															@click.stop
															@change="updateTag($event, box.uuid)">
														<option v-for="(tag, indx) in filteredTags" 
																:value="indx"
																:key="indx">
															{{ tag.name }}
														</option>
													</select>
													
													<button class="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 transition-colors hover:bg-gray-50 rounded" 
															@click.stop="removeElement(box.uuid)">
														<Trash2Icon class="w-5 h-5 mx-auto"/>
													</button>
												</div>
											</div>

										</li>

									</template>

									<!-- DIVISION SECTION -->
									<li id="eMdivision" class="nav selected" @click="toggleMenuSection('eMdivision')">
										<span class="text-sm text-gray-700">{{ $t('Division') }}</span>
										<span class="arrow-icon"></span>
									</li>
									<li class="menu-content eMdivision sel">
										<div class="py-2">
											<select v-model="layout.action.edit.set" class="custom-select">
												<template v-if="!layout.action.edit.set">
													<option :value="layout.action.edit.set">{{ $t('Not defined') }}</option>
												</template>
												<option value="PREDETERMINED">{{ $t('Predetermined') }}</option>
												<option value="TRAIN">{{ $t('Train') }}</option>
												<option value="TEST">{{ $t('Test') }}</option>
												<option value="VALIDATION">{{ $t('Validation') }}</option>
											</select>
										</div>
									</li>

									<!-- COMMENTS SECTION -->
									<li id="eMcomments" class="nav selected" @click="toggleMenuSection('eMcomments')">
										<span class="text-sm text-gray-700">{{ $t('Comments') }}</span>
										<span class="arrow-icon"></span>
									</li>
									<li class="menu-content eMcomments sel">
										<div class="w-full">
											<textarea id="object-image-comments"
												spellcheck="false"
												v-model="layout.action.edit.comments"
												class="w-full h-20 p-3 mt-2 mb-1 text-sm bg-gray-50 text-gray-600 rounded-md border-gray-300 shadow-sm resize-none focus:border-gray-300 focus:ring-0 focus:ring-offset-0 focus:outline-none"
												placeholder="Add comments about this image..."></textarea>
										</div>
									</li>

									<!-- ATTRIBUTES SECTION -->
									<li id="eMfile" class="nav selected" @click="toggleMenuSection('eMfile')" v-if="layout.action.edit?.gimageObj">
										<span class="text-sm text-gray-700">{{ $t('Attributes') }}</span>
										<span class="arrow-icon"></span>
									</li>
									<li class="menu-content eMfile sel">
										<table class="w-full">
											<tbody>
												<tr class="border-b border-gray-100">
													<td class="py-2 text-gray-600">{{ $t('Type') }}</td>
									<td class="py-2 text-right text-gray-800">{{ layout.action.edit.gimageObj?.meta?.contentType }}</td>
												</tr>
												<tr class="border-b border-gray-100">
													<td class="py-2 text-gray-600">{{ $t('URL') }}</td>
													<td class="py-2 text-right">
														<a class="text-blue-500 hover:underline text-sm truncate block max-w-[200px]" 
															style="color: var(--primary-500, #3664b3) !important;"
															:href="layout.action.edit.gimageObj?.url"
											target="_blank">{{ layout.action.edit.gimageObj?.meta?.name }}</a>
													</td>
												</tr>
												<tr v-if="layout.canvas && layout.canvas.size && Object.keys(layout.canvas.size).length" class="border-b border-gray-100">
													<td class="py-2 text-gray-600">{{ $t('Dimensions') }}</td>
									<td class="py-2 text-right text-gray-800">{{ layout.canvas.size?.width || 0 }} x {{ layout.canvas.size?.height || 0 }}</td>
												</tr>
											</tbody>
										</table>
									</li>

								</ul>
							</div>
						</div>

					</div>
				</div>

			</div>

		</div>

        <CreateTagModal v-if="showCreateTagModal" 
            interface="editor"
            :dataset="layout.dataset"
            @tagCreated="reloadTags()"
            @closeModal="showCreateTagModal = false"/>

        <CopyImageModal v-if="showCopyModal" 
            :imageId="layout.action.edit.id"
            :datasets="layout.datasets" 
            @closeModal="showCopyModal = false"/>

        <DeleteImageModal v-if="showDeleteModal" 
            :image="{ name: layout.action.edit.name, id: layout.action.edit.id }" 
            @imageDeleted="showDeleteModal = false; fromDelete = true; $emit('imageDeleted')"
            @closeModal="showDeleteModal = false"/>

        <DownloadImageModal v-if="showDownloadModal" 
            :image="layout.action.edit"
            :canvas="canvasObjects" 
            @closeModal="showDownloadModal = false"/>

        <EditorPopup v-if="showSamIntroPopup"
            :show="showSamIntroPopup"
            @close="showSamIntroPopup = false"/>

    </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, computed, toRaw, watch, nextTick } from "vue";
import { useRouter }		        from "vue-router";
import { useStore } 		        from "@/store";
import { fabric } 			        from 'fabric';
import { helper as $helper }        from "@/utils/helper";
import { v4 as uuidv4 }             from 'uuid';
import cash 				        from "cash-dom";
import lzstring 			        from 'lz-string';

import { initFabricHistory }        from '@/fabric/history.js';
import { createLabel, createPath }  from '@/fabric/objects.js';

import { 
    setGlobals, 
    setupPanning, 
    scrollToElement, 
    removeElement, 
    updateCanvasIdsFromPaths,
    toggleCrosshairLines
} from '@/fabric/global.js';

import { 
    setupDrawBB, 
    setupMoveBB 
} from '@/fabric/events/detection.js';

import { 
    setupDrawingPath, 
    setupEraserPath, 
    setupPolygonDrawing,
    cleanupPolygonDrawing,
    getCanvasId, 
    updateSelectedCanvas,
    cleanupSegmentationResources
} from '@/fabric/events/segmentation.js';

import { 
    setupSAMDrawing, 
    autoDetection as autoDetectionSAM, 
    setPredictedMask,
    activateSAM,         
    cleanupSAM,
    teardownSAMDrawing,
    SAMActive,           
    samSteps
} from '@/fabric/events/sam.js';

import {
    handleVLMInference,
    autoDetectionVLM,
    cleanupVLM,
    deactivateVLM,
    activateVLM,
    VLMActive,
    vlmProcessing,
    autoNewImage,
    stepTimes
} from '@/fabric/events/vlm.js';

import * as rosepetalModel	    from "rosepetal-model";
import config 				    from '@/etc/rosepetal.json';

import NavigatorCmp  		    from "@/components/manage-image/EditorNavigator.vue";
import EditorToolbarCmp         from '@/components/manage-image/EditorToolbar.vue';
import EditorFiltersCmp         from '@/components/manage-image/EditorFilters.vue';
import EditorPopup              from '@/components/manage-image/EditorPopup.vue';

import CreateTagModal 	        from "@/modals/tag/CreateTag.vue";
import CopyImageModal           from "@/modals/image/CopyImage.vue";
import DeleteImageModal         from "@/modals/image/DeleteImage.vue";
import DownloadImageModal       from "@/modals/image/DownloadImage.vue";


const emit = defineEmits(['closeEditor', 'closeEditorFromCopy']);

// ----- SETUP ----- //
let $dataset 	= rosepetalModel.default.dataset;
let $image 		= rosepetalModel.default.image;
let $sam 		= rosepetalModel.default.sam;
let $vlm        = rosepetalModel.default.vlm;

const props = defineProps({
    options: {
        type: Object,
        required: true,
    }
});

const showTags = computed(() => {
    if (layout.value.dataset && layout.value.dataset.tags) {
        if (layout.value.dataset.type == 'imageObjectDetection')
            return layout.value.navFilters.objtagsType == 'labeled';
        else if (layout.value.dataset.type == 'MULTILABEL')
            return layout.value.navFilters.objtagsType == 'anomaly';
        return true;
    }
    return false;
});

const showLabeling = computed(() => {
    return layout.value.dataset.type === 'MULTICLASS' || 
           layout.value.dataset.type === 'imageObjectDetection' || 
           layout.value.dataset.type === 'ANOMALY' ||
           (layout.value.dataset.type === 'MULTILABEL' && layout.value.status.multilabelTag?.id == '0');
});

const filteredTags = computed(() => {
    let tags = {};
    
    if (layout.value.dataset && layout.value.dataset.tags) {
        switch (layout.value.dataset.type) {
            case 'MULTILABEL':
                tags = Object.fromEntries(
                    Object.entries(layout.value.dataset.tags)
                          .filter(([_, tag]) => !tag?.normal && tag?.id !== '0')
                );
                break;
            case 'MULTICLASS':
            case 'imageObjectDetection':
            case 'ANOMALY':
                tags = Object.fromEntries(
                    Object.entries(layout.value.dataset.tags)
                          .filter(([_, tag]) => !tag?.unclassified)
                );
                break;
            default:
                tags = {};
        }
    }

    if (Object.keys(tags).length === 0) {
        showCreateTagModal.value = true;
    }
    
    return tags;
});

const normalButtonEnabled = computed(() => {
    if (canvasObjects.value && canvasObjects.value.length) {
        const hasAnnotations = canvasObjects.value.some(obj => 
            obj.elementType !== "background" && 
            obj.elementType !== "crosshair" && 
            obj.name !== 'cursorBrush'
        );
        
        return !hasAnnotations;
    }
    return true;
});

const currentBackground = computed(() => {
    if (canvasObjects.value.length) {
        return canvasObjects.value.find((obj) => obj.elementType === "background");
    }
    return false;
});

const currentObjects = computed(() => {
    if (canvasObjects.value.length) {
        return canvasObjects.value.filter((obj) => 
            obj.elementType !== "background" && 
            obj.elementType !== "crosshair"
        );
    }
    return [];
});

const tagCounts = computed(() => {
    return canvasObjects.value.reduce((counts, obj) => {
        if (!["mask", "bbRect"].includes(obj.elementType)) return counts;

        const tagId = obj.id || obj.name;
        if (tagId) {
            counts[tagId] = (counts[tagId] || 0) + 1;
    }
    
    return counts;
    }, {});
});


const enableSAM             = true;
const enableVLM             = true;

const router   				= useRouter();
const store 				= useStore();
const uuid					= computed(() => store.state.main.uuid);

const showCreateTagModal    = ref(false);
const showCopyModal         = ref(false);
const showDeleteModal       = ref(false);
const showDownloadModal     = ref(false);
const showSamIntroPopup     = ref(false);

let canvasbox               = false;
let canvasboxRef            = ref(null);

const canvasObjects         = ref([]);
const canvasIds 			= ref([]);
const trackManualTags 		= ref(false);

const isInitialImageLoad    = ref(true);

const navigatorRef          = ref(null);
const editorToolbarRef      = ref(null);

const lastKnownPointer      = ref(null);

const currentIMG_URI        = ref(null);


// ----- CONSTANTS & SETTINGS ----- //

const defaultPathOpt = {
    fill: 			    null,
    shadow: 		    null,
    erasable: 		    true,
    strokeUniform:      true,
    evented: 		    true,
    selectable: 	    false,
    paintFirst: 	    'fill',
    strokeLineCap:	    'round',
    strokeLineJoin:	    'round',
    elementType:        'mask',
};

const layout = ref({ 
    dataset:        {},
    datasets: 		[],
    selectedCanvas:	false,
    selectedBB:		false,
    status: {
        currentTag:     false,
        multilabelTag:  false
    },
    action: { 
        edit: false 
    },
    navFilters: {
        objtagsType:    'all',
        objByTag:       'all',
        order:          'date',
        direction:      'desc'
    },
    canvas: { 
        loading:    false,
        error:      false,
        drawing:    true,
        showMasks:  true,
        showBoxes:  true,
        isDown:     false,
        viewport: {
            zoom:           null,
            transform:      null,
            persistEnabled: false
        },
        filters: {
            opacity:    0.5,
            brightness: 0,
            contrast:   0,
            saturation: 0,
            vibrance:   0
        },
        tools:	{
            currentTool:        false,
            previousTool:       false,
            zoom:           	false,
            panning:            false,
            autoDetection:      true,
            modalBrush:     	{ type: "line", size: 30  },
            modalBoundigBox:	{ type: "drawBB" },
            modalDetection: 	{ 
                api:                            "sam", 
                loading:                        false,
                error:                          false,
                predictedPoints:                [], 
                predictedBoundingBox:           [],
                previousPredictedPoints:        [],
                previousPredictedBoundingBox:   []
            }
        }
    }
});


// ----- EVENT HANDLERS ----- //

const handleToggleChange = (event) => {
    if (event.target.checked) {
        layout.value.status.multilabelTag = layout.value.dataset.tags['OK'] || layout.value.dataset.tags['0'];
        setupTool('panning');
    } else {
        layout.value.status.currentTag      = layout.value.dataset.tags[0];
        layout.value.status.multilabelTag   = layout.value.dataset.tags['0'];
    }
    saveImage(true, true);
}

const handleKeyDown = async (e) => {
    if (e.target.matches('input, textarea, [contenteditable]')) {
        return;
    }

    if (e.ctrlKey) {
        if (e.key.toLowerCase().match(/[zy]/)) {
            handleUndoRedo(e);
        } else if (layout.value.canvas.tools.currentTool !== 'samDetection') {
            if (e.repeat) return;

            const datasetType = layout.value.dataset.type;
            if (datasetType === 'MULTICLASS' || datasetType === 'ANOMALY') return;

            layout.value.canvas.tools.previousTool = layout.value.canvas.tools.currentTool;
            switch (layout.value.canvas.tools.currentTool) {
                case 'panning':
                    layout.value.canvas.tools.currentTool = datasetType === 'imageObjectDetection' ? 'drawBB' : 'brush';
                    break;

                case 'drawBB':
                case 'brush':
                    layout.value.canvas.tools.currentTool = 'panning';
                    break;
                    
                default:
                    layout.value.canvas.tools.currentTool = 'panning';
                    break;
            }

            setupTool(layout.value.canvas.tools.currentTool);
        }
    }
    
    else if (e.key === "Tab") {
        e.preventDefault();
        if (layout.value.canvas.tools.currentTool === 'polygon') return;
        if (enableSAM) setPredictedMask();
    }
    
    else if (e.key === "Alt") {
        e.preventDefault();
        if (e.repeat) return;

        const datasetType = layout.value.dataset.type;
        if (datasetType === 'MULTICLASS' || datasetType === 'ANOMALY') return;

        layout.value.canvas.tools.previousTool = layout.value.canvas.tools.currentTool;
        switch (layout.value.canvas.tools.currentTool) {
            case 'panning':
                layout.value.canvas.tools.currentTool = datasetType === 'imageObjectDetection' ? 'moveBB' : 'eraser';
                break;

            case 'drawBB':
                layout.value.canvas.tools.currentTool = 'moveBB';
                break;

            case 'moveBB':
                layout.value.canvas.tools.currentTool = 'drawBB';
                break;

            case 'brush':
                layout.value.canvas.tools.currentTool = 'eraser';
                break;

            case 'eraser':
                layout.value.canvas.tools.currentTool = 'brush';
                break;

            default:
                layout.value.canvas.tools.currentTool = 'panning';
                break;
        }

        setupTool(layout.value.canvas.tools.currentTool);
    }
    
    else if (e.key === "Delete" || e.key === "Supr" || e.key === "Del") {
        handleDelete();
    } 
    
    else if (e.key >= '0' && e.key <= '9') {
        handleNumberKey(e);
    } 
    
    else if (e.key.toLowerCase() === 'e' && layout.value.dataset.type === 'MULTILABEL') {
        layout.value.canvas.tools.currentTool = 'eraser';
        setupTool(layout.value.canvas.tools.currentTool);
    }
}

const handleKeyUp = async (e) => {
    if (e.target.matches('input, textarea, [contenteditable]')) {
        return;
    }
    
    if (e.key !== 'Control' && e.key !== 'Alt') return;

    e.preventDefault();
    if (e.repeat) return;

    if (layout.value.canvas.tools.currentTool !== 'samDetection') {
        layout.value.canvas.tools.currentTool = layout.value.canvas.tools.previousTool;
        layout.value.canvas.tools.previousTool = false;

        setupTool(layout.value.canvas.tools.currentTool);
    }
}

const handleUndoRedo = (e) => {
    const isUndo = !e.shiftKey && e.key.toLowerCase() === 'z';
    const isRedo = (e.shiftKey && e.key.toLowerCase() === 'z') || e.key.toLowerCase() === 'y';
    
    if (!canvasbox || !canvasbox.historyUndo || !canvasbox.historyRedo) return;
    
    e.preventDefault();
    
    if (isUndo && canvasbox.historyUndo.length > 2) {
        performHistoryOperation('undo');
    } else if (isRedo && canvasbox.historyRedo.length > 0) {
        performHistoryOperation('redo');
    }
}

const handleDelete = () => {
    const activeObject = canvasbox.getActiveObject();
    if (!activeObject) return;

    if (activeObject.type === 'activeSelection') {
        activeObject.forEachObject(obj => removeElement(obj.uuid));
    } else {
        removeElement(activeObject.uuid);
    }
    
    canvasbox.discardActiveObject();
    canvasbox.requestRenderAll();
}

const numberKeyBuffer = ref('');
const numberKeyTimeout = ref(null);

const handleNumberKey = (e) => {
    if (layout.value.dataset.type !== 'MULTICLASS' && layout.value.dataset.type !== 'ANOMALY') return;

    if (numberKeyTimeout.value) {
        clearTimeout(numberKeyTimeout.value);
    }

    numberKeyBuffer.value += e.key;
    
    const selectTag = () => {
        const selectedIndex = parseInt(numberKeyBuffer.value, 10) - 1;
        const tags = Object.values(layout.value.dataset.tags);
        
        if (selectedIndex >= 0 && selectedIndex < tags.length) {
            layout.value.status.currentTag = tags[selectedIndex];
            saveImage(true, true);
            
            nextTick(() => {
                const tagElement = document.getElementById(`tag-card-${layout.value.status.currentTag.id}`);
                if (tagElement) {
                    tagElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
            });
        }
        
        numberKeyBuffer.value = '';
        numberKeyTimeout.value = null;
    };

    numberKeyTimeout.value = setTimeout(selectTag, 300);
}

const handleResize = async function() {
    if (canvasbox) { 
        let canvasElement = document.getElementById('canvasBox');
        if (!canvasElement) return;

        const currentZoom = canvasbox.getZoom();
        const currentViewportTransform = [...canvasbox.viewportTransform];
        
        let canvasWidth = canvasElement.clientWidth;
        let canvasHeight = canvasElement.clientHeight;

        canvasbox.setWidth(canvasWidth);
        canvasbox.setHeight(canvasHeight);
        
        canvasbox.setViewportTransform(currentViewportTransform);
        
        const upperCanvas = canvasbox.upperCanvasEl;
        if (upperCanvas) {
            upperCanvas.style.width = '100%';
            upperCanvas.width = canvasWidth;
        }
        
        canvasbox.calcOffset();
        
        layout.value.canvas.tools.zoom = currentZoom;
        
        layout.value.canvas.viewport = {
            zoom:           currentZoom,
            transform:      [...canvasbox.viewportTransform],
            persistEnabled: layout.value.canvas.viewport.persistEnabled
        };
        
        if (layout.value.canvas.drawing) {
            handleBrushSize();
        }
        
        canvasbox.renderAll();
    }
}

const handleZoom = (zoomLevel) => {
    if (!canvasbox) return;
    
    canvasbox.zoomToPoint(new fabric.Point(canvasbox.width / 2, canvasbox.height / 2), zoomLevel);
    layout.value.canvas.tools.zoom = zoomLevel;
    
    layout.value.canvas.viewport = {
        zoom:           canvasbox.getZoom(),
        transform:      [...canvasbox.viewportTransform],
        persistEnabled: layout.value.canvas.viewport.persistEnabled,
        baseScale:      layout.value.canvas.viewport.baseScale || canvasbox.getZoom()
    };

    if (lastKnownPointer.value) {
        if (!layout.value.canvas.drawing && layout.value.canvas.tools.currentTool !== 'panning') {
            toggleCrosshairLines(true, lastKnownPointer.value);
        } else {
            toggleCrosshairLines(false);
        }
    } else if (!layout.value.canvas.drawing && layout.value.canvas.tools.currentTool !== 'panning') {
        toggleCrosshairLines(true, {x:0, y:0});
    }
    
    canvasbox.renderAll();
    
    if (layout.value.canvas.drawing) {
        handleBrushSize();
    }
}

const handleBrushSize = () => {
    if (!canvasbox || !canvasbox.freeDrawingBrush) return;
    setupTool(layout.value.canvas.tools.currentTool);
}

const handleMouseWheel = (e) => {
    if (e.defaultPrevented || e.e.buttons !== 0) return;
    if (!canvasbox || layout.value.canvas.loading) return;

    e.e.preventDefault();

    const zoomStep = e.e.ctrlKey ? 0.1 : 0.05;
    const delta = Math.sign(e.e.deltaY) * -1;

    const currentZoom = canvasbox.getZoom();
    const baseScale = layout.value.canvas.viewport?.baseScale || currentZoom;
    
    const minZoom = baseScale * 0.2;
    const maxZoom = baseScale * 3;
    
    let newZoom = currentZoom + zoomStep * delta;
    newZoom = Math.round(Math.min(Math.max(minZoom, newZoom), maxZoom) * 100) / 100;

    if (newZoom === currentZoom) return;

    const zoomPoint = new fabric.Point(e.e.offsetX, e.e.offsetY);
    canvasbox.zoomToPoint(zoomPoint, newZoom);

    layout.value.canvas.tools.zoom = newZoom;
    layout.value.canvas.viewport = {
        zoom: newZoom,
        transform: [...canvasbox.viewportTransform],
        persistEnabled: layout.value.canvas.viewport.persistEnabled,
        baseScale: baseScale
    };

    const currentWheelPointer = canvasboxRef.value.getPointer(e.e);
    lastKnownPointer.value = currentWheelPointer;

    if (!layout.value.canvas.drawing && layout.value.canvas.tools.currentTool !== 'panning' && currentWheelPointer) {
        toggleCrosshairLines(true, currentWheelPointer);
    } else {
        toggleCrosshairLines(false);
    }
}

const handleObjectsUpdate = () => {
    const seenUUIDs = new Set();
    canvasbox.getObjects().forEach(obj => {
        if (obj.uuid) {
            if (seenUUIDs.has(obj.uuid)) {
                canvasbox.remove(obj);
            } else {
                seenUUIDs.add(obj.uuid);
            }
        }
    });
    canvasObjects.value = canvasbox.getObjects();
}


// ----- CANVAS SETUP ----- //

const toggleMenuSection = (menuId) => {
    const menuElement = document.getElementById(menuId);
    if (!menuElement) return;
    
    menuElement.classList.toggle('selected');
    
    const menuContents = document.getElementsByClassName(menuId);
    Array.from(menuContents).forEach(content => {
        content.style.transition = 'max-height 0.3s ease-in-out, padding 0.3s ease-in-out';
        content.classList.toggle('sel');
    });
}

const setupCanvasEvents = () => {
    canvasbox.on('object:added', async function(e) {
        if (!e.target.excludeFromExport && e.target.elementType !== 'crosshair') {
            if (layout.value.dataset.type === 'MULTILABEL' && 
                layout.value.status.multilabelTag?.id === 'OK' && 
                (e.target.elementType === 'mask' || e.target.elementType === 'bbRect')) {
                layout.value.status.multilabelTag = layout.value.dataset.tags['0'];
            }
            handleObjectsUpdate();
        }
    });

    canvasbox.on('object:removed', function(e) {
        if (!e.target.excludeFromExport) {
            handleObjectsUpdate();
        }
    });

    canvasbox.on('object:modified', function(e) {
        if (!e.target.excludeFromExport) {
            canvasbox._pushHistory();
            startAutoSaveTimer();
            canvasbox.renderAll();
        }
    });
}

const setupCurrentTag = () => {
    if (layout.value.dataset.type === 'MULTICLASS' || layout.value.dataset.type === 'ANOMALY') {
        layout.value.status.currentTag = layout.value.action.edit.tag;
    } else {
        if (!layout.value.status.currentTag) {
            layout.value.action.currentTag = layout.value.dataset.tags[0];
        }

        if (layout.value.dataset.type == 'MULTILABEL') { 
            const tagId = layout.value.action.edit.tag?.id;
            const tagObj = layout.value.dataset.tags[tagId];

            layout.value.status.multilabelTag = tagObj;
        }
    }
}

const setupCurrentTool = () => {
    if (!layout.value.canvas.tools.currentTool) {
        switch (layout.value.dataset.type) {
            case 'MULTICLASS':
            case 'ANOMALY':
                layout.value.canvas.tools.currentTool = 'panning';
                break;
            case 'MULTILABEL':
                layout.value.canvas.tools.currentTool = 'brush';
                break;
            case 'imageObjectDetection':
                layout.value.canvas.tools.currentTool = 'drawBB';
                break;
            default:
                layout.value.canvas.tools.currentTool = 'panning';
        }
    }

    setupCanvasEvents();
    setupTool(layout.value.canvas.tools.currentTool);
}

const setupTool = (tool) => {
    const previous = layout.value.canvas.tools.currentTool;

    if (previous === 'samDetection' && tool !== 'samDetection') {
        teardownSAMDrawing();
    }

    if (previous === 'polygon' && tool !== 'polygon') {
        cleanupPolygonDrawing();
    }

    layout.value.canvas.tools.currentTool = tool || layout.value.canvas.tools.currentTool;

    switch (tool) {
        case 'panning':
            setupPanning(canvasbox, layout.value);
            break;

        case 'brush': {
            if (layout.value.selectedCanvas) {
                layout.value.status.currentTag = layout.value.dataset.tags[layout.value.selectedCanvas.name];
            }

            if (!layout.value.status.currentTag) {
                break;
            }

            const currentTagId  = layout.value.status.currentTag.id;
            const tagData       = layout.value.dataset.tags[currentTagId];
            const color         = tagData?.color || '#000000';

            const opt = {
                tagId:          currentTagId,
                rawColor:       color,
                cursorSize:     layout.value.canvas.tools.modalBrush.size / canvasbox.getZoom(),
                cursorColor:    $helper.hexToRgb(color || '#000000', layout.value.canvas.filters.opacity),
            }

            setupDrawingPath(opt);
            break;
        }

        case 'polygon': {
            if (layout.value.selectedCanvas) {
                layout.value.status.currentTag = layout.value.dataset.tags[layout.value.selectedCanvas.name];
            }

            if (!layout.value.status.currentTag) {
                break;
            }

            const currentTagId  = layout.value.status.currentTag.id;
            const tagData       = layout.value.dataset.tags[currentTagId];
            const color         = tagData?.color || '#000000';

            const opt = {
                tagId:          currentTagId,
                rawColor:       color,
                opacity:        layout.value.canvas.filters.opacity,
            };
            
            setupPolygonDrawing(opt);
            break;
        }

        case 'eraser': {
            const opt = {
                cursorSize:     layout.value.canvas.tools.modalBrush.size,
                cursorColor:    $helper.hexToRgb('#000000', 0.4)
            }

            setupEraserPath(opt);
            break;
        }

        case 'drawBB':
            setupDrawBB();
            break;

        case 'moveBB':
            setupMoveBB();
            break;

        case 'samDetection':
            activateSAM();
            setupSAMDrawing(config.firebaseConfig.projectId);
            break;

        case 'vlmDetection':
            setupPanning(canvasbox, layout.value);
            break;

        default:
            break;
    }
}

const setupGimageObj = async (img) => {
    if (!img.fabricImage) {
        layout.value.action.edit.gimageObj = img.gimageObj || await $image.getStorageUrl(img.uri);
        const { error, url } = layout.value.action.edit.gimageObj;
        if (error) {
            layout.value.canvas.error = true;
        } else {
            layout.value.action.edit.gimage = url || false;
        }
    }
}


// ----- HISTORY SYSTEM ----- //

const performHistoryOperation = (operation) => {
    const currentViewportTransform = [...canvasbox.viewportTransform];
    
    canvasbox.renderOnAddRemove = false;
    
    const currentBackground = canvasbox.getObjects().find(obj => obj.elementType === 'background');
    const isHighRes = currentBackground && currentBackground._fromOriginal;
    
    canvasbox[operation](() => {
        canvasbox.setViewportTransform(currentViewportTransform);
        
        if (isHighRes) {
            const newBackground = canvasbox.getObjects().find(obj => obj.elementType === 'background');
            if (newBackground && !newBackground._fromOriginal && layout.value.action.edit.fabricImage) {
                canvasbox.remove(newBackground);
                canvasbox.insertAt(layout.value.action.edit.fabricImage, 0);
            }
        }
        
        normalizeAllPaths();
        updateCanvasIdsFromPaths();
        handleObjectsUpdate();
        
        canvasbox.discardActiveObject();
        canvasbox.renderOnAddRemove = true;
        
        requestAnimationFrame(() => {
            normalizeAllPaths();
            canvasbox.requestRenderAll();
            setTimeout(() => {
                normalizeAllPaths();
                canvasbox.requestRenderAll();
            }, 10);
        });
        
        setTimeout(() => { startAutoSaveTimer(); }, 50);
    });
}

const normalizeAllPaths = () => {
    canvasbox.getObjects().forEach(obj => {
        if (obj.type === 'path') {
            obj.set({
                strokeUniform: true,
                scaleX: 1,
                scaleY: 1
            });
            
            obj.setCoords();
        }
    });
}


// ----- ELEMENTS FUNCTIONS ----- //

const hoverDelay                = 150;
const hoverTimerId              = ref(null);
const currentHighlightedUuid    = ref(null);

const hideMasks = async (remove = false) => {
    if (!canvasbox) return;
    if (remove) {
        if (confirm('Are you sure you want to remove all masks?')) {
            const snapshot = JSON.stringify(canvasbox.toJSON(['uuid', 'uuid_parent', 'name', 'labeled', 'stroke', 'color', 'elementType']));
            window.__lastSnapshotBeforeRemoval = snapshot;
            currentObjects.value.forEach(obj => removeElement(obj.uuid));
        }
    } else {
        currentObjects.value.forEach(obj => obj.set({ visible: layout.value.canvas.showMasks  }));
        canvasbox.renderAll();
    }
}

const hideBoxes = (remove = false) => {
    if (!canvasbox) return;
    if (remove) {
        if (confirm('Are you sure you want to remove all bounding boxes?')) {
            const objects = canvasbox.getObjects();
            const boundingBoxes = objects.filter(obj => obj.elementType === 'bbRect');
            boundingBoxes.forEach(box => {
                const relatedObjects = objects.filter(obj => obj.uuid === box.uuid || obj.uuid_parent === box.uuid);
                relatedObjects.forEach(obj => canvasbox.remove(obj));
            });
            canvasbox.renderAll();
            handleObjectsUpdate();
            canvasbox._pushHistory();
        }
    } else {
        const objects = canvasbox.getObjects();
        const boundingBoxes = objects.filter(obj => obj.elementType === 'bbRect');
        boundingBoxes.forEach(box => {
            const relatedObjects = objects.filter(obj => obj.uuid === box.uuid || obj.uuid_parent === box.uuid);
            relatedObjects.forEach(obj => obj.set({ visible: layout.value.canvas.showBoxes }));
        });
        canvasbox.renderAll();
    }
}

const highlightElement = async (uuid, show = false) => {
    if (hoverTimerId.value) {
        clearTimeout(hoverTimerId.value);
        hoverTimerId.value = null;
    }

    if (show) {
         hoverTimerId.value = setTimeout(() => {
             if (currentHighlightedUuid.value === uuid) {
                currentHighlightedUuid.value = null;
                currentObjects.value.forEach(obj => {
                    obj.set({ visible: true });
                });
                canvasbox.renderAll();
                
                const elementId = uuid.toString();
                const element = document.getElementById(elementId);
                if (element) {
                    element.classList.remove('item-selected');
                }
            }
        }, hoverDelay);
    } else {
        currentHighlightedUuid.value = uuid;
        currentObjects.value.forEach(obj => {
            if (obj.uuid !== uuid && obj.uuid_parent !== uuid) {
                obj.set({ visible: false });
            } else {
                obj.set({ visible: true });
            }
        });
        
        canvasbox.renderAll();
        
        const elementId = uuid.toString();
        const element = document.getElementById(elementId);
        if (element) {
            element.classList.add('item-selected');
        }
    }
}

const selectBoundingBox = (uuid) => {
    setupTool('moveBB');

    if (layout.value.selectedBB === uuid) {
        layout.value.selectedBB = null;
        canvasbox.discardActiveObject();
        canvasbox.renderAll();
        return;
    }
    
    const box = canvasbox.getObjects().find(obj => obj.uuid === uuid && obj.elementType === 'bbRect');
    if (box) {
        layout.value.selectedBB = uuid;
        canvasbox.discardActiveObject();
        canvasbox.setActiveObject(box);
        scrollToElement(uuid);
        canvasbox.renderAll();
    }
}


// ----- TAGS MANAGER ----- //

const reloadTags = async () => { 
    showCreateTagModal.value = false;
    layout.value.dataset.tags = await $dataset.getTags(layout.value.dataset.id);

    if (layout.value.dataset.type === 'MULTILABEL' && !layout.value.status.currentTag) {
        editorToolbarRef.value.selectTag();
    }
}

const updateTag = (event, uuid) => {
    const tagId = event.target.value;
    const box = currentObjects.value.find(obj => obj.uuid === uuid && obj.elementType === 'bbRect');
    
    if (box) {
        const tag = layout.value.dataset.tags[tagId];
        if (tag) {
            box.set({
                id:     tagId,
                name:   tag.name,
                stroke: tag.color,
                color:  tag.color,
                fill:   $helper.hexToRgb(tag.color, layout.value.canvas.filters.opacity),
            })
            box.setCoords();
            canvasbox.renderAll();
            canvasbox._pushHistory();
        }
    }
}


// ----- CANVAS MANAGER ----- //

const canvasInit = async () => {
    try {
        const defaultOptions = {
            originX:                'left',
            originY:                'top',
            centeredScaling:        false,
            uniformScaling:         false,
            transparentCorners:     true,
            selection:              false,
            selectable:             false,
            evented:                false,
            allowTouchScrolling:    false
        };

        const objectDetectionOptions = {
            perPixelTargetFind:     true,
            enableRetinaScaling:    true,
            imageSmoothingEnabled:  true,
        };

        const dsType = layout.value.dataset.type;
        const canvaOpt = {
            ...defaultOptions,
            ...(dsType === 'imageObjectDetection' ? objectDetectionOptions : {})
        };

        canvasbox = new fabric.Canvas('c', canvaOpt);
        canvasboxRef.value = canvasbox;

        setGlobals(canvasbox, layout, canvasIds, currentObjects, currentBackground);
        initFabricHistory();

        canvasbox._historyInit();
        canvasbox._historyNextState = canvasbox.toDatalessJSON();

        let canvasElement = document.getElementById('canvasBox');
        if (!canvasElement) {
            await new Promise(resolve => {
                const checkElement = () => {
                    canvasElement = document.getElementById('canvasBox');
                    if (canvasElement) {
                        resolve();
                    } else {
                        setTimeout(checkElement, 10);
                    }
                };
                checkElement();
            });
        }
        
        if (canvasElement && canvasElement.clientWidth && canvasElement.clientHeight) {
        canvasbox.setWidth(canvasElement.clientWidth);
        canvasbox.setHeight(canvasElement.clientHeight);
        } else {
            canvasbox.setWidth(800);
            canvasbox.setHeight(600);
        }
        
        canvasbox.calcOffset();
        canvasbox.on('mouse:wheel', handleMouseWheel);

        $helper.dragElement(document.getElementById("canvasImageTools"));

        return canvasbox;
    } catch (error) {
        console.error("Canvas initialization error:", error);
        throw error;
    }
}

const canvasLoadImage = async (image, executionId) => {
    canvasClear();

    return loadImage(image, executionId).then(img => {
        if (executionId !== currentEditExecutionId) return;

        layout.value.canvas.size = { width: img.width, height: img.height };
        layout.value.canvas.backImageOriginalSize = {
            width:      img.width,
            height:     img.height,
            blobImage:  img
        };

        if (executionId !== currentEditExecutionId) return;

        canvasLoadImageData(image, img, executionId);
        initialCenterImage();
    });
}

const canvasLoadImage_o = async (image) => {
    canvasClear();

    return loadImage(image).then(img => {
        layout.value.canvas.size = { width: img.width, height: img.height };
        layout.value.canvas.backImageOriginalSize = { width: img.width, height: img.height, blobImage: img };

        initialCenterImage();
        canvasLoadImageData(image, img);

        canvasbox.renderAll();
        canvasbox._historyInit();
        canvasbox._historyRecording = true;
        canvasbox._pushHistory();
    });
}

const canvasLoadImageData = async (image, img, executionId) => {
    const datasetType = layout.value.dataset.type;
    
    if (executionId !== currentEditExecutionId) return;

    if (datasetType === 'MULTILABEL') {
        await loadMasks(image, executionId);
    } else if (datasetType === 'imageObjectDetection') {
        const opt = {
            imgWidth: img.width,
            imgHeight: img.height
        };
        await loadBoundingBoxes(image, opt, executionId);
    } else {
        console.warn(`Unhandled dataset type: ${datasetType}`);
        return;
    }

    canvasbox.renderAll();
    canvasbox._historyInit();
    canvasbox._historyRecording = true;
    canvasbox._pushHistory();
}

const canvasLoadImageData_o = (image, img) => {
    if (layout.value.dataset.type === 'MULTILABEL') {
        loadMasks(image);
    } else if (layout.value.dataset.type === 'imageObjectDetection') {
        const opt = {
            imgWidth:   img.width, 
            imgHeight:  img.height
        };
        loadBoundingBoxes(image, opt);
    }
}

const canvasClear = () => {
    if (canvasbox) {
        cleanupSegmentationResources();
        
        canvasbox.clear();
        canvasbox.renderAll();
        canvasIds.value = [];

        canvasbox._clearHistory();
        canvasbox._historyRecording = false;
    }
}


// ----- ZOOM SYSTEM CORRECTION ----- //

const initialCenterImage = (reset = false) => {
    let image = canvasbox.getObjects().find(obj => obj.elementType === 'background');
    if (!image) return;

    const { persistEnabled, zoom, transform } = layout.value.canvas.viewport;
    
    if (persistEnabled && zoom && transform && !reset) {
        canvasbox.setZoom(zoom);
        canvasbox.setViewportTransform(transform);
        layout.value.canvas.tools.zoom = zoom;
        return;
    }

    canvasbox.setViewportTransform([1, 0, 0, 1, 0, 0]);
    
    const zoomMargins = 0.1;
    const zoomX = (canvasbox.width / image.width) - zoomMargins;
    const zoomY = (canvasbox.height / image.height) - zoomMargins;
    const fitToScreenScale = Math.min(zoomX, zoomY, 1);
    
    const imageArea = image.width * image.height;
    const canvasArea = canvasbox.width * canvasbox.height;
    const areaRatio = imageArea / canvasArea;
    
    const imageAspectRatio = image.width / image.height;
    const canvasAspectRatio = canvasbox.width / canvasbox.height;
    const aspectRatioDiff = Math.abs(imageAspectRatio - canvasAspectRatio);
    
    let baseScale;
    if (areaRatio < 0.08) {
        baseScale = Math.min(3.0, 1.2 / Math.sqrt(areaRatio));
    } else if (areaRatio < 0.25) {
        baseScale = Math.min(2.5, 1.0 / Math.sqrt(areaRatio));
    } else if (areaRatio < 0.6) {
        const aspectBonus = aspectRatioDiff > 1 ? 1.6 : 1.0;
        baseScale = Math.min(1.8 * aspectBonus, 0.9 / Math.sqrt(areaRatio));
    } else {
        baseScale = fitToScreenScale * (aspectRatioDiff > 0.5 ? 1.45 : 1.0);
    }
    
    const minScale = 0.2;
    const actualScale = Math.max(minScale, baseScale);
    
    if (!layout.value.canvas.viewport.baseScale) {
        layout.value.canvas.viewport.baseScale = actualScale;
    }

    const imageCenterX = image.left + image.width / 2;
    const imageCenterY = image.top + image.height / 2;
    
    const viewportCenterX = canvasbox.width / 2;
    const viewportCenterY = canvasbox.height / 2;

    const translateX = viewportCenterX - imageCenterX * actualScale;
    const translateY = viewportCenterY - imageCenterY * actualScale;

    layout.value.canvas.tools.zoom = actualScale;
    canvasbox.setViewportTransform([actualScale, 0, 0, actualScale, translateX, translateY]);
    
    layout.value.canvas.viewport = {
        zoom:           actualScale,
        transform:      [...canvasbox.viewportTransform],
        persistEnabled: layout.value.canvas.viewport.persistEnabled,
        baseScale:      actualScale
    };
    
    canvasbox.calcOffset();
    canvasbox.renderAll();
}

const resetZoom = () => {
    initialCenterImage(true);
    if (layout.value.canvas.viewport.persistEnabled) {
        layout.value.canvas.viewport = {
            zoom:           canvasbox.getZoom(),
            transform:      [...canvasbox.viewportTransform],
            persistEnabled: true,
            baseScale:      layout.value.canvas.viewport.baseScale || canvasbox.getZoom()
        };
    }
}


// ----- IMAGE TREATMENT ----- //

let currentEditExecutionId = 0;

const loadImage = async (image, executionId) => {
    canvasbox._historyRecording = false;

    if (executionId !== currentEditExecutionId) return null;

    if (image.fabricImage) {
        canvasbox.insertAt(image.fabricImage, 0);
        canvasbox.renderAll();
    } else {
        const lowRes = image.imageData?.lowResImg || image.gimage;
        if (lowRes) {
            image.fabricImage = await new Promise((resolve) => {
                fabric.Image.fromURL(lowRes, (img) => {
                    if (!img || executionId !== currentEditExecutionId) return resolve(null);

                    Object.assign(img, {
                        elementType: 'background',
                        hoverCursor: 'grab',
                        erasable: false,
                        selectable: false,
                        evented: false,
                    });

                    canvasbox.insertAt(img, 0);
                    canvasbox.renderAll();

                    resolve(img);
                }, {
                    crossOrigin: 'anonymous',
                    onerror: () => {
                        console.error('❌ Error cargando imagen low-res');
                        resolve(null);
                    }
                });
            });

            if (executionId !== currentEditExecutionId) return null;
        }
    }

    const imgSrc = image.imageData?.reducedImg || image.imageData?.originalImg;
    if (imgSrc) {
        fabric.Image.fromURL(imgSrc, (highResImg) => {
            if (!highResImg || executionId !== currentEditExecutionId) return;

            const wasRecording = canvasbox._historyRecording;
            canvasbox._historyRecording = false;

            Object.assign(highResImg, {
                elementType:    'background',
                hoverCursor:    'grab',
                erasable:       false,
                selectable:     false,
                evented:        false,
                _fromOriginal:  true
            });

            const currentBg = canvasbox.getObjects().find(obj => obj.elementType === 'background');
            if (currentBg) canvasbox.remove(currentBg);

            canvasbox.insertAt(highResImg, 0);
            canvasbox.renderAll();

            image.fabricImage = highResImg;

            canvasbox._historyRecording = wasRecording;
        }, {
            crossOrigin: 'anonymous',
            onerror: () => {
                console.error('❌ No se pudo cargar la imagen original');
            }
        });
    }

    return image.fabricImage;
}

const loadImage_o = async (image) => {
    canvasbox._historyRecording = false;

    if (image.fabricImage) {
        canvasbox.insertAt(image.fabricImage, 0);
        canvasbox.renderAll();
    } else {
        const lowRes = image.imageData?.lowResImg || image.gimage;
        if (lowRes) {
            image.fabricImage = await new Promise((resolve) => {
                fabric.Image.fromURL(lowRes, (img) => {
                    if (!img) return resolve(null);

                    Object.assign(img, {
                        elementType:    'background',
                        hoverCursor:    'grab',
                        erasable:       false,
                        selectable:     false,
                        evented:        false,
                    });

                    canvasbox.insertAt(img, 0);
                    canvasbox.renderAll();

                    resolve(img);
                }, {
                    crossOrigin: 'anonymous',
                    onerror: () => {
                        console.error('❌ Error cargando imagen low-res');
                        resolve(null);
                    }
                });
            });
        }
    }

    const imgSrc = image.imageData?.reducedImg || image.imageData?.originalImg;
    if (imgSrc) {
        fabric.Image.fromURL(imgSrc, (highResImg) => {
            const wasRecording = canvasbox._historyRecording;
            canvasbox._historyRecording = false;

            Object.assign(highResImg, {
                elementType:    'background',
                hoverCursor:    'grab',
                erasable:       false,
                selectable:     false,
                evented:        false,
            });

            const currentBg = canvasbox.getObjects().find(obj => obj.elementType === 'background');
            if (currentBg) canvasbox.remove(currentBg);

            canvasbox.insertAt(highResImg, 0);
            canvasbox.renderAll();

            image.fabricImage = highResImg;

            canvasbox._historyRecording = wasRecording;
        }, {
            crossOrigin: 'anonymous',
            onerror: () => {
                console.error('❌ No se pudo cargar la imagen original');
            }
        });
    }

    return image.fabricImage;
}

const editImage = async (imageId) => { 
    const executionId = ++currentEditExecutionId;

    let img = $helper.isObject(imageId) ? imageId : await $image.get(imageId);

    if (executionId !== currentEditExecutionId) return;
    if (!img || !img.id) return;
    currentIMG_URI.value = img.uri;
    
    if (img) {
        allowAutoSave.value = false;
        layout.value.canvas.loading = true;

        isInitialImageLoad.value    = true;
        
        layout.value.action.edit = img;

        setupCurrentTag();
        await setupGimageObj(img);

        if (!canvasbox) await canvasInit();
        await canvasLoadImage(layout.value.action.edit, executionId);
        
        handleObjectsUpdate();
        
        setupCurrentTool();

        if (enableSAM && SAMActive.value) {
            autoDetectionSAM(currentIMG_URI.value);
        }

        if (enableVLM && VLMActive.value) {
            autoDetectionVLM(currentIMG_URI.value);
        }

        router.push('/datasets/' + layout.value.dataset.id + '/' + img.id);
        
        layout.value.canvas.loading = false;

        if (img.updatedDate) autoSaveStatus.value = 'Last saved: ' + img.updatedDate;
        
        setTimeout(() => {
            isInitialImageLoad.value = false;
            allowAutoSave.value = true;
        }, 200);
    }
}

const saveImage = async (isAutoSaveOrOptions = false, multiLabelTagChanged = false) => {
    const isAutoSave    = typeof isAutoSaveOrOptions === 'boolean' ? isAutoSaveOrOptions : false;
    const skipAutoSave  = typeof isAutoSaveOrOptions === 'object' ? isAutoSaveOrOptions?.skipAutoSave : false;

    if (!canvasbox || (layout.value.canvas.loading && !isAutoSave && !skipAutoSave)) {
        return false;
    }

    if (!layout.value.action.edit || !layout.value.action.edit.id) {
        return false;
    }

    if (canvasbox.historyUndo.length < 1 && layout.value.dataset.type === 'imageObjectDetection') {
        return false;
    }
    
    if (canvasbox.historyUndo.length < 2 && layout.value.dataset.type === 'MULTILABEL' && !multiLabelTagChanged) {
        return false;
    }

    if (!isAutoSave && !skipAutoSave) {
        layout.value.canvas.loading = true;
    }

    const saveStartTime = performance.now();
    
    try {
        let img = currentBackground.value;
        let saveError = false;

        canvasbox.discardActiveObject();

        if (!layout.value.action.edit || !layout.value.action.edit.id) {
            return false;
        }

        const opt = {
            imageId: 	layout.value.action.edit.id.toString(),
            imageName:	layout.value.action.edit.name ? layout.value.action.edit.name.split('/').pop() : '',
            datasetId: 	layout.value.dataset.id.toString(),
            projectId: 	config.firebaseConfig.projectId
        }
        
        switch (layout.value.dataset.type) {
            case 'MULTICLASS':
            case 'ANOMALY':
                opt.tag = layout.value.status.currentTag.id;
                saveError = await $image.saveClassificationImage(opt);
                break;

            case 'MULTILABEL': {
                const originalMasks = canvasbox.getObjects().filter(obj => obj.elementType === 'mask');
                const saveMasks = originalMasks.map(mask => {
                    const maskClone = fabric.util.object.clone(mask);
                    maskClone.opacity = 1;
                    return maskClone;
                });
                opt.tagId = layout.value.status.multilabelTag.id;
                saveError = await $image.saveSegmentationImage(img, saveMasks, opt);
                break;
            }

            case 'imageObjectDetection': {
                const detectionObjs = currentObjects.value.filter(obj => obj.elementType === 'bbRect');
                opt.tags = layout.value.dataset.tags;
                opt.backImageOriginalSize = layout.value.canvas.backImageOriginalSize;
                saveError = await $image.saveDetectionImage(detectionObjs, opt);
                break;
            }
        }

        if (!saveError) {
            const { set: imageSet, comments: imageComments } = layout.value.action.edit;
            await $image.setSet(opt.imageId, imageSet);
            await $image.setComments(opt.imageId, imageComments ? imageComments : '');
            if (!isAutoSave && !skipAutoSave) layout.value.canvas.loading = false;
        }

        const saveEndTime = performance.now();
        const saveTimeMs = saveEndTime - saveStartTime;
        saveCount.value++;

        return !saveError;
    } catch (error) {
        const saveEndTime = performance.now();
        console.error(`Save error after ${(saveEndTime - saveStartTime).toFixed(2)}ms:`, error);
        return false;
    }
}


// ----- IMAGE DATA LOADER ----- //

const loadMasks = async (image, executionId) => {
    if (executionId !== currentEditExecutionId) return;

    const { masks } = image;
    const opacity       = layout.value.canvas.filters.opacity;
    const datasetTags   = layout.value.dataset.tags;

    if (!masks || !Array.isArray(masks) || !masks.length) return;

    for (const mask of masks) {
        if (executionId !== currentEditExecutionId) return;

        const tagId = mask.tag.id;
        const tagData = datasetTags[tagId];
        if (!tagData) continue;

        const tagName = tagData.name;
        const canvasId = getCanvasId(tagId);

        if (!mask.paths || !Array.isArray(mask.paths)) continue;

        const restoredPaths = await Promise.all(
            mask.paths.map(compressedPath => {
                const pathData = JSON.parse(lzstring.decompressFromBase64(compressedPath));
                return new Promise(resolve => {
                    fabric.util.enlivenObjects([pathData], function(objects) {
                        const restoredObject = objects[0];
                        const originalFill = restoredObject.fill;
                        Object.assign(restoredObject, {
                            ...defaultPathOpt,
                            uuid:           uuidv4(),
                            uuid_parent:    canvasId,
                            id:             tagId,
                            name:           tagName,
                            opacity:        opacity
                        });
                        if (originalFill) {
                            restoredObject.fill = originalFill;
                        }
                        resolve(restoredObject);
                    });
                });
            })
        );

        canvasbox.add(...restoredPaths);
    }
}

const loadMasks_o = async (image, opt = false) => {
    const { masks, mask } = image;
    const opacity = layout.value.canvas.filters.opacity;

    if (masks && masks.objects?.length > 0) {
        let promises = masks.objects.map(mask => {
            if (mask.paths) {
                const canvasId = getCanvasId(mask.name);
                const color = mask.stroke;
                
                const paths = mask.paths.map(compressedPath => {
                    const pathData = JSON.parse(lzstring.decompressFromBase64(compressedPath));
                    return new fabric.Path(pathData.path, {
                        ...defaultPathOpt,
                        uuid:           uuidv4(),
                        uuid_parent:    canvasId,
                        name:           mask.name,
                        color:          color,
                        stroke:         color,
                        strokeWidth:    pathData.strokeWidth,
                        opacity:        opacity
                    });
                });

                canvasbox.add(...paths);
                return Promise.resolve();
            } else if (mask.url) {
                return new Promise((resolve) => {
                    fabric.Image.fromURL(mask.url, (img) => {
                        img.set({
                            ...defaultPathOpt,
                            uuid:           uuidv4(),
                            uuid_parent:    getCanvasId(mask.name),
                            name:           mask.name,
                            color:          mask.stroke,
                            stroke:         mask.stroke,
                            opacity:        opacity,
                            scaleX:         img.scaleX,
                            scaleY:         img.scaleY
                        });

                        if (layout.value.dataset.type === "imageObjectDetection") {
                            img.set({ evented: false, opacity: opacity });
                        }

                        canvasbox.bringToFront(img);
                        resolve();
                    }, { crossOrigin: 'anonymous' });
                });
            }
        });
        
        await Promise.all(promises);
    } else if (mask && Object.keys(mask).length > 0) {
        let maskJSON = false;

        try {
            maskJSON = JSON.parse(mask.imageJson);
        } catch (error) {
            maskJSON = JSON.parse(lzstring.decompressFromUint8Array(mask.imageJson.toUint8Array()));
        }

        if (maskJSON) {
            canvasbox.loadFromJSON(maskJSON, canvasbox.renderAll.bind(canvasbox), function(_, img) {
                img.set({
                    ...defaultPathOpt,
                    uuid:           uuidv4(),
                    uuid_parent:    getCanvasId(img.name),
                    color:          opt?.color || img.stroke,
                    stroke:         opt?.color || img.stroke,
                    opacity:        opacity
                });
            });
        }
    }
}

const loadBoundingBoxes = async (image, opt = false, executionId) => {
    if (!image.tags || !Array.isArray(image.tags)) return;
    if (executionId !== currentEditExecutionId) return;

    const opacity = layout.value.canvas.filters.opacity || 0.5;
    const manualTracking = trackManualTags.value;
    const datasetTags = layout.value.dataset.tags;

    for (const boundingBox of image.tags) {
        if (executionId !== currentEditExecutionId) return;
        if (boundingBox.type !== "rect") continue;

        const tagId = boundingBox.tag.id;
        const tagData = datasetTags[tagId];
        if (!tagData) continue;

        const tagName = tagData.name;
        const bbColor = (boundingBox.labeled === 'manual' && manualTracking)
            ? '#ff0000'
            : (tagData.color || 'transparent');

        const labelOpt = {
            id:     tagId,
            name:   tagName,
            color:  bbColor,
            fill:   $helper.hexToRgb(bbColor, opacity),
            height: boundingBox.h * opt.imgHeight,
            width:  boundingBox.w * opt.imgWidth,
            top:    boundingBox.y * opt.imgHeight,
            left:   boundingBox.x * opt.imgWidth,
        };

        const bbRect = createLabel(labelOpt);
        canvasbox.add(bbRect);
    }

    if (executionId === currentEditExecutionId) {
        canvasbox.renderAll();
    }
}

const loadBoundingBoxes_o = async (image, opt = false) => {
    if (!image.tags) return;

    const opacity = layout.value.canvas.filters.opacity || 0.5;
    const manualTracking = trackManualTags.value;
    const datasetTags = layout.value.dataset.tags;

    for (const boundingBox of image.tags) {
        if (boundingBox.type !== "rect") continue;

        const tagId = boundingBox.tag.id;
        const tagData = datasetTags[tagId];
        if (!tagData) continue;

        const tagName = tagData.name;
        const bbColor = (boundingBox.labeled === 'manual' && manualTracking) ? '#ff0000' : (tagData.color || 'transparent');

        const labelOpt = {
            id:     tagId,
            name:   tagName,
            color:  bbColor,
            fill:   $helper.hexToRgb(bbColor, opacity),
            height: boundingBox.h * opt.imgHeight,
            width:  boundingBox.w * opt.imgWidth,
            top:    boundingBox.y * opt.imgHeight,
            left:   boundingBox.x * opt.imgWidth,
        };

        const bbRect = createLabel(labelOpt);
        canvasbox.add(bbRect);
    }

    canvasbox.renderAll();
}


// ----- UI CONTROLS ----- //

const fromDelete = ref(false);

const clearEditor = async () => {
    if (layout.value.action.edit.gimage) layout.value.action.edit.gimage = false;
    if (canvasbox) {
        canvasbox.clear();
        canvasbox.dispose();
        canvasbox = false;
    }

    layout.value.canvas.drawing = true;
    layout.value.canvas.tools.zoom = false;
    layout.value.canvas.tools.modalBrush = { size: 30, type: "line" };
    layout.value.canvas.tools.modalBoundigBox = { type: "drawBB" };
    layout.value.canvas.filters.opacity = 0.5;
}

const exitEditor = async () => { 
    if (canvasbox && layout.value.action.edit && layout.value.action.edit.id && !fromDelete.value) saveImage(true);
    await clearEditor();
    
    if (document.getElementById('BtnLabelingReload')) {
        setTimeout(async () => { document.getElementById('BtnLabelingReload').click(); }, 5);
        router.push('/datasets/' + props.options.dataset.id + '/labeling');
    } else { 
        router.push('/datasets/' + props.options.dataset.id); 
    }

    emit("closeEditor");
    return true;
}


// ----- VLM FUNCTIONS ----- //

const hasChangesToSave = () => {
    if (!canvasbox || !allowAutoSave.value || isInitialImageLoad.value) {
        return false;
    }

    if (!layout.value.action.edit || !layout.value.action.edit.id) {
        return false;
    }

    if (layout.value.dataset.type === 'MULTICLASS' || layout.value.dataset.type === 'ANOMALY') {
        return true;
    }

    const historyLength = canvasbox.historyUndo?.length || 0;

    if (layout.value.dataset.type === 'imageObjectDetection') {
        return historyLength >= 1;
    }
    
    if (layout.value.dataset.type === 'MULTILABEL') {
        return historyLength >= 2;
    }

    return false;
}

const nextImage = async () => {
    if (navigatorRef.value && typeof navigatorRef.value.nextImage === 'function') {
        await navigatorRef.value.nextImage();
    } else {
        console.warn('El componente Navigator no está listo o no expone nextImage.');
    }
}

// ----- AUTOSAVE MANAGEMENT ----- //

const saveCount         = ref(0);
const autoSaveStatus    = ref('');
const autoSaveTimeout   = ref(null);
const lastSaveTime      = ref(null);
const isSaving          = ref(false);
const allowAutoSave     = ref(false);

const setupAutoSave = () => {
    if (canvasbox) {
        canvasbox._onHistoryChange = () => {
            if (!isInitialImageLoad.value && allowAutoSave.value) {
                startAutoSaveTimer();
            }
        };
    }
}

const startAutoSaveTimer = () => {
    if (isSaving.value || isInitialImageLoad.value || !allowAutoSave.value) return;

    clearTimeout(autoSaveTimeout.value);

    autoSaveStatus.value    = 'Saving image...';
    autoSaveTimeout.value   = setTimeout(performAutoSave, 500);
}

const performAutoSave = async () => {
    if (!canvasbox || isSaving.value || isInitialImageLoad.value || !allowAutoSave.value) return;
    
    isSaving.value = true;
    autoSaveStatus.value = 'Saving image...';
    
    try {
        const saveResult = await saveImage(true);
        
        if (saveResult) {
            const now = new Date();
            const formattedTime = $helper.getTimestampDate(now, 'full');
            
            autoSaveStatus.value = 'Last saved: ' + formattedTime;
            
            layout.value.action.edit.updatedAt = now;
            layout.value.action.edit.updatedDate = formattedTime;
            
            lastSaveTime.value = now;
        } else {
            autoSaveStatus.value = 'Save failed';
        }
    } catch (error) {
        console.error('Auto-save failed:', error);
        autoSaveStatus.value = 'Save failed';
    } finally {
        isSaving.value = false;
    }
}


// ----- COMPONENT LIFECYCLE ----- //

onMounted(async () => { 
    window.addEventListener('resize', handleResize);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    document.addEventListener('selectstart', (e) => {
        const canvasBox = document.getElementById('canvasBox');
        if (canvasBox && canvasBox.contains(e.target)) {
            e.preventDefault();
            return false;
        }
    }, { passive: false });

    document.addEventListener('touchmove', (e) => {
        const canvasBox = document.getElementById('canvasBox');
        if (canvasBox && canvasBox.contains(e.target) && e.touches.length > 1) {
            e.preventDefault();
        }
    }, { passive: false });

    document.addEventListener('gesturestart', (e) => {
        const canvasBox = document.getElementById('canvasBox');
        if (canvasBox && canvasBox.contains(e.target)) {
            e.preventDefault();
        }
    }, { passive: false });

    if (props.options.filters)      layout.value.navFilters = { ...layout.value.navFilters, ...props.options.filters };
    if (props.options.currentPage)  layout.value.currentPage = props.options.currentPage;

    layout.value.dataset	    = props.options.dataset;
    layout.value.dataset.tags   = props.options.dataset.tags || await $dataset.getTags(layout.value.dataset.id);
    layout.value.datasets	    = props.options.datasetsList;

    await editImage(props.options.id);
    setupAutoSave();

    if (enableSAM && layout.value.dataset.type !== 'MULTICLASS' && layout.value.dataset.type !== 'ANOMALY') {
        const localPreference = localStorage.getItem('rosepetal-sam-popup-pref');
        showSamIntroPopup.value = !localPreference;
    }
});

onUnmounted(async () => {
    window.removeEventListener('resize', handleResize);
    window.removeEventListener('keydown', handleKeyDown);
    window.removeEventListener('keyup', handleKeyUp);

    await exitEditor();
    
    cleanupVLM();
    cleanupSAM();
    cleanupSegmentationResources();
});

watch(SAMActive, (isActive) => {
    if (!isActive && layout.value.canvas.tools.currentTool === 'samDetection') {
        setupTool('panning');
    }
});
</script>

<style scoped>
/* BASE STYLES */
.fullSizeEditor { 
  position: fixed; 
  left: 0; 
  top: 0; 
  width: 100%; 
  height: 100%; 
  background-color: #fff; 
    z-index: 99999; 
  overflow: hidden;
}

/* HIDE ALL SCROLLBARS GLOBALLY */
* {
  -ms-overflow-style: none !important;
  --primary-100: #d2dff0;
  --primary-200: #a7bfe4;
  --primary-300: #7c9fd7;
  --primary-400: #517fc9;
  --primary-500: #3664b3;
  --primary-600: #1e4591;
  --primary-700: #17356e;
  --primary-800: #10254c;
  --primary-900: #081529;
}

/* MENU STYLES */
.rpEditorMenu {
  width: 100%;
  font-size: 14px;
  background-color: #fff;
  position: relative;
  z-index: 1;
}

.rpEditorMenu li {
  background-color: #f8fafc;
  width: 100%;
  cursor: pointer;
  padding: 14px 16px;
  display: block;
  transition: all 0.2s ease;
}

.rpEditorMenu ul li:hover {
  background-color: #f1f5f9;
}

.rpEditorMenu ul li.nav,
.rpEditorMenu ul li.selected {
  background-color: #f8fafc !important;
  padding-bottom: 0.5rem;
}

.rpEditorMenu ul li.nav:hover,
.rpEditorMenu ul li.selected:hover {
  background-color: #f1f5f9 !important;
}

.rpEditorMenu ul li.nav.selected {
  background-color: #f1f5f8 !important;
  font-weight: 500;
}

/* STICKY CONTROLS */
.sticky-controls {
  display: block;
  width: 100%;
  background-color: #fff;
  z-index: 10;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
}

/* MENU CONTENT */
.menu-content {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease-out;
  background-color: #fff;
  padding: 0;
  margin: 0;
  font-size: 14px;
  cursor: default;
  position: relative;
  z-index: 5;
}

.menu-content.sel {
  max-height: 500px; 
  padding: 0;
  margin: 0;
  margin-top: -2px;
  border-top: 0;
  transition: max-height 0.3s ease-in;
  overflow-y: auto;
  background-color: #fff;
  position: relative;
  padding-left: 1rem;
  padding-right: 1rem;
  padding-bottom: 0.5rem;
}

.menu-content.sel::-webkit-scrollbar {
  width: 0; 
  display: none;
}

.menu-content .scrollable-content {
  padding: 0.5rem 2rem;
}

/* ARROW ICON */
.arrow-icon {
  display: inline-block;
  width: 16px;
  height: 16px;
  margin-top: 2px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  transition: transform 0.2s ease;
  transform: rotate(0deg);
}

.rpEditorMenu ul li.selected .arrow-icon {
  transform: rotate(180deg);
}

.rpEditorMenu ul li.menu-content {
  background-color: #fff;
  padding: 0.5rem 2rem;
  font-size: 14px;
  display: none;
  cursor: default;
}

.rpEditorMenu ul li.menu-content.sel {
  display: block;
  padding: 0.5rem 2rem;
}

/* FORM ELEMENTS */
.custom-select {
  width: 100%;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background-color: #f9fafb;
  color: #4b5563;
  font-size: 14px;
  transition: all 0.2s ease;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-size: 16px;
  background-position: right 8px center;
  background-repeat: no-repeat;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

.custom-select:hover {
  border-color: #cbd5e1;
}

.custom-input {
  width: 100%;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background-color: #f9fafb;
  color: #4b5563;
  font-size: 14px;
  transition: all 0.2s ease;
}

.custom-input:hover {
  border-color: #cbd5e1;
}

/* ACTION BUTTONS */
.action-button {
  padding: 6px 10px;
  border-radius: 6px;
  transition: all 0.2s ease;
    font-weight: normal;
    font-size: 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  border: 1px solid transparent;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.bg-gray-50 .grid-cols-4 .action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  transition: all 0.15s ease;
  background-color: transparent;
  border-radius: 0;
  border: none;
  cursor: pointer;
  height: 40px;
  overflow: hidden;
}

@media (max-width: 1300px) {
  .action-button {
    padding: 5px 8px;
    font-size: 13px;
  }
  
  .action-button .w-4 {
    width: 14px;
  }
  
  .action-button .h-4 {
    height: 14px;
  }
  
  .action-button .mr-1 {
    margin-right: 2px;
  }
}

.bg-gray-50 .grid-cols-4 .action-button:hover {
  background-color: var(--primary-50, #edf2fa);
  box-shadow: inset 0 -1px 0 var(--primary-300, #7c9fd7);
}

.bg-gray-50 .grid-cols-4 .action-button:active {
  background-color: var(--primary-100, #d2dff0);
}

.bg-gray-50 .grid-cols-4 .action-icon {
  width: 16px; 
  height: 16px;
  color: var(--primary-600, #1e4591);
  stroke-width: 1.5px;
  opacity: 0.85;
}

.bg-gray-50 .grid-cols-4 .action-button:hover .action-icon {
  opacity: 1;
}

.bg-gray-50 .grid-cols-4 .action-button-danger {
  color: #dc2626;
}

.bg-gray-50 .grid-cols-4 .action-button-danger .action-icon {
  color: #dc2626;
}

.bg-gray-50 .grid-cols-4 .action-button-danger:hover {
  background-color: #fee2e2;
  box-shadow: inset 0 -1px 0 #fca5a5;
}

.bg-gray-50 .grid-cols-4 .action-button-danger:active {
  background-color: #fecaca;
}

.action-button.primary {
  background-color: var(--primary-50, #edf2fa);
  color: var(--primary-600);
  border-color: var(--primary-200);
}

.action-button.primary:hover {
  background-color: var(--primary-100);
}

.action-button.secondary {
  background-color: #f3f4f6;
  color: #4b5563;
  border-color: #e5e7eb;
}

.action-button.secondary:hover {
  background-color: #e5e7eb;
}

.action-button.danger {
  background-color: #fee2e2;
  color: #dc2626;
  border-color: #fecaca;
}

.action-button.danger:hover {
  background-color: #fecaca;
}

/* TOP BAR */
.editorTopTools {
  position: relative;
  top: auto;
  left: auto;
  display: flex;
  align-items: center;
  background: var(--primary-700);
  color: white;
  font-size: 13px;
  height: 36px;
  z-index: 999;
  border-bottom-right-radius: 12px;
  backdrop-filter: blur(8px);
  overflow-x: auto;
  width: auto;
  max-width: 100%;
}

.editorTopTool {
  padding: 6px 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  white-space: nowrap;
  font-weight: normal;
  min-width: fit-content;
}

@media (max-width: 1300px) {
  .editorTopTools {
    height: 32px;
    font-size: 12px;
  }
  
  .editorTopTool {
    padding: 4px 8px;
  }
}

.editorTopTool:last-child {
  border-right: none;
  border-bottom-right-radius: 12px;
}

.editorTopTool :deep(svg) {
  width: 14px;
  height: 14px;
  opacity: 0.8;
}

/* MODAL STYLES */
.modal-image-editor{ width: 100% !important;min-width: 600px; margin: 0 !important; height: 100% !important;}
.modal-image-editor .modal-content{ height: 100% !important;}
#canvasContent {
    display: flex;
    justify-content: center; 
    width: 100%;
    margin-left: 0;
    overflow: visible;
}
#canvasContent .canvas-container{ 
    width: 100% !important;
    overflow: visible !important;
}

#editorRightPanel{
    --tw-bg-opacity: 1;
    background-color: rgba(255, 255, 255, var(--tw-bg-opacity)); 
    overflow-y: scroll;
}

@media (min-width: 1280px) {
  #editorRightPanel {
    width: 100%;
  }
}

/* SECTION TITLES */
.rpEditorMenu li.nav {
    font-size: 15px;
    color: #374151;
    padding: 0.5rem 2rem;
    padding-bottom: 0.25rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: #f8fafc;
    position: relative;
    z-index: 11;
    margin: 0;
    border-bottom: 0;
}

.rpEditorMenu ul li.nav span {
  font-weight: 500;
}

/* CONTENT TEXT */
.rpEditorMenu li.menu-content {
  font-size: 14px;
  color: #4B5563;
  padding: 16px 18px;
}

/* TABLE STYLES */
#editorRightPanel table td {
  font-size: 14px;
  padding-top: 8px;
  padding-bottom: 8px;
}

/* LABEL STYLES */
#editorRightPanel label {
  font-size: 14px;
  color: #4B5563;
}

/* FOCUS STYLES */
button:focus,
input:focus,
select:focus,
textarea:focus {
  outline: none !important;
  box-shadow: none !important;
  border-color: #e2e8f0 !important;
}

/* COMMENTS AREA */
#object-image-comments {
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  color: #4B5563;
  background-color: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  resize: none;
  height: 100px;
}

#object-image-comments:focus,
textarea#object-image-comments:focus,
#editorRightPanel #object-image-comments:focus,
.eMcomments #object-image-comments:focus,
.menu-content #object-image-comments:focus {
  outline: none !important;
  box-shadow: none !important;
  border-color: #E5E7EB !important;
  --tw-ring-color: transparent !important;
  --tw-ring-offset-width: 0 !important;
  --tw-ring-offset-color: transparent !important;
  --tw-ring-opacity: 0 !important;
}

/* ATTRIBUTES SECTION */
.eMfile td:first-child {
  color: #374151;
  font-weight: 500;
}

.eMfile td:last-child {
  color: #4B5563;
}

/* SECTION HEADER */
#eMfile.nav {
  background-color: #f1f5f9;
}

/* MODAL DIALOG */
.modal-dialog {
  max-width: 520px;
  margin-top: 50px !important;
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
}

.modal-dialog.modal-md {
  width: 470px;
}

.modal-content {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border: none;
}

.modal-content .flex.items-center.justify-between {
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
  background-color: #f9fafb;
}

.modal-content h3 {
  font-size: 16px;
  font-weight: 500;
  color: var(--primary-600, #1e4591);
}

.modal-body {
  padding: 20px !important;
}

.modal-body .text-lg {
  font-size: 16px;
  font-weight: 500;
  color: #374151;
  margin-top: 0 !important;
  margin-bottom: 16px !important;
}

.modal-body .bg-gray-50 {
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
}

.modal-body .text-base,
.modal-body .text-lg,
.modal-body div[class*="text-"] {
  font-size: 16px;
  line-height: 1.5;
}

.modal-body .text-sm {
  font-size: 14px;
  line-height: 1.5;
}

/* MODAL BUTTONS */
.modal-btn-primary,
.modal-btn-secondary,
.modal-btn-danger {
  padding: 10px 24px;
  border-radius: 6px;
  transition: all 0.2s ease-in-out;
  font-weight: normal;
  font-size: 14px;
  min-width: 120px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.modal-btn-secondary {
  background-color: #f3f4f6;
  color: #4b5563;
  border: none;
}

.modal-btn-secondary:hover {
  background-color: #e5e7eb;
}

.modal-btn-primary {
  background-color: var(--primary-600, #1e4591);
  color: white;
  border: none;
}

.modal-btn-primary:hover {
  background-color: var(--primary-700, #17356e);
}

.modal-btn-danger {
  background-color: #ef4444;
  color: white;
  border: none;
}

.modal-btn-danger:hover {
  background-color: #dc2626;
}

/* CHECKBOX STYLES */
input[type="checkbox"] {
  border-radius: 4px;
  border-color: #cbd5e1;
}

input[type="checkbox"]:checked {
  background-color: var(--primary-600);
  border-color: var(--primary-600);
}

/* SLIDER CUSTOMIZATION */
:deep(.slider-blue .slider-connect) {
  background: var(--primary-600, #1e4591) !important;
  border-radius: 999px !important;
}

:deep(.slider-blue .slider-handle) {
  width: 16px !important;
  height: 16px !important;
  background: var(--primary-600, #1e4591) !important;
  border: 2px solid white !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2) !important;
  border-radius: 9999px !important;
  top: -6px !important;
  transition: all 0.2s ease !important;
}

:deep(.slider-blue .slider-handle:hover) {
  background: var(--primary-700, #17356e) !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3) !important;
}

:deep(.slider-blue .slider) {
  height: 4px !important;
  background: #e5e7eb !important;
  border-radius: 999px !important;
}

.slider-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  line-height: 1.2;
}

.slider-header label,
.slider-header .section-title {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.slider-value {
  font-size: 14px;
  color: #4b5563;
  font-weight: normal;
}

.slider-control {
  margin-bottom: 16px;
}

/* METADATA BAR */
#metadata-bar {
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  background: var(--primary-700);
  color: white;
  font-size: 0.8rem;
  height: 1.3rem;
  z-index: 999;
  border-bottom-right-radius: 8px;
  backdrop-filter: blur(4px);
}

#metadata-bar > div {
  padding: 0.5rem 0.5rem;
  display: flex;
  align-items: center;
  border-right: 1px solid rgba(255, 255, 255, 0.15);
  white-space: nowrap;
}

#metadata-bar > div:last-child {
  border-right: none;
  border-bottom-right-radius: 8px;
}

#metadata-bar :deep(svg) {
  width: 14px;
  height: 14px;
}

/* TOGGLE SWITCH */
.image-type-toggle {
  padding: 0 0 0 1.8%;
  width: 60%;
}

.toggle-container {
  width: 100%;
  position: relative;
}

.toggle-input {
  height: 0;
  width: 0;
  visibility: hidden;
  position: absolute;
}

.toggle-input:checked + .toggle-switch .toggle-slider {
  transform: translateX(calc(100% + 6px));
}

.toggle-input:checked + .toggle-switch .toggle-option.normal {
  color: white;
  font-weight: 500;
}

.toggle-input:not(:checked) + .toggle-switch .toggle-option.anomaly {
  color: white;
  font-weight: 500;
}

.toggle-input:focus + .toggle-switch {
  box-shadow: 0 0 0 2px rgba(30, 69, 145, 0.2);
}

.toggle-switch:hover {
  background-color: #f8fafc;
}

::-webkit-scrollbar {
    width: 0;
    height: 0;
    display: none;
}

* {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

* ::-webkit-scrollbar {
    display: none;
}

.save-notification {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: white;
  padding: 10px 16px;
  border-radius: 8px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  z-index: 9999;
  max-width: 300px;
}

.save-notification-content {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #1f2937;
}

#canvasBox.drawing {
    -webkit-touch-callout: none !important;
    -webkit-user-select: none !important;
    -khtml-user-select: none !important;
    -moz-user-select: none !important;
    -ms-user-select: none !important;
    user-select: none !important;
}

#canvasBox.drawing * {
    -webkit-touch-callout: none !important;
    -webkit-user-select: none !important;
    -khtml-user-select: none !important;
    -moz-user-select: none !important;
    -ms-user-select: none !important;
    user-select: none !important;
}

:deep(#canvasBox),
:deep(#canvasBox *),
:deep(.canvas-container),
:deep(canvas) {
  -webkit-user-select: none !important;
  -moz-user-select: none !important;
  -ms-user-select: none !important;
  user-select: none !important;
  outline: none !important;
}

:deep(::selection) {
  background: transparent !important;
}

/* AVAILABLE TAGS */
#eMlabeling {
  overflow: hidden;
}

#eMlabeling .available-tags {
  max-width: 100%;
  overflow: hidden;
}

#eMlabeling button {
  max-width: 100%;
}

/* TABLE STYLES */
#editorRightPanel table td {
  font-size: 14px;
}

/* FILTER SELECT STYLES */
.filter-select {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  border: 1px solid #e5e7eb;
  background-color: white;
  max-width: 120px;
  min-width: 80px;
  height: 26px;
  color: #4B5563;
}

.filter-select option {
  font-size: 0.75rem;
  padding: 2px;
}

/* COMPACT TOGGLE SWITCH */
.nav .toggle-container {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 4px 0;
}

.nav .toggle-switch {
  width: 100%;
  height: 36px;
  background-color: #f3f4f6;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  position: relative;
  display: flex;
  overflow: hidden;
  cursor: pointer;
}

.toggle-input:disabled + .toggle-switch {
  cursor: not-allowed;
  opacity: 0.6;
}

.nav .toggle-option {
  font-size: 14px;
  line-height: 36px;
  font-weight: 400;
  flex: 1;
  text-align: center;
  z-index: 2;
  transition: color 0.3s ease;
  user-select: none;
}

.nav .toggle-slider {
  position: absolute;
  top: 2px;
  left: 2px;
  width: calc(50% - 6px);
  height: 30px;
  background-color: var(--primary-600, #1e4591);
  border-radius: 4px;
  transition: transform 0.3s ease;
  z-index: 1;
}

.nav .toggle-input:checked + .toggle-switch .toggle-slider {
  left: auto;
  right: 3px;
  transform: none;
}

.nav .toggle-input:checked + .toggle-switch .toggle-option.normal {
  color: white;
  font-weight: 500;
}

.nav .toggle-input:not(:checked) + .toggle-switch .toggle-option.anomaly {
  color: white;
  font-weight: 500;
}

::-webkit-scrollbar {
    width: 0;
    height: 0;
    display: none;
}

* {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

* ::-webkit-scrollbar {
    display: none;
}

.sticky-top-controls {
  position: sticky;
  top: 0;
  z-index: 20;
  background-color: #fff;
  box-shadow: none;
  padding: 0;
  margin: 0;
  width: 100%;
  border: none;
}

.sticky-top-controls .flex.items-center {
  font-weight: 500;
  padding: 0 8px;
}

.menu-content.sel {
  max-height: 500px; 
  padding: 0;
  margin: 0;
  transition: max-height 0.3s ease-in;
  overflow-y: auto;
  background-color: #fff;
  position: relative;
}

/* HIGHLIGHT ANIMATION */
.highlight-selected {
  animation: highlight-pulse 0.8s cubic-bezier(0.4, 0, 0.6, 1) 1;
}

@keyframes highlight-pulse {
  0%, 100% {
    background-color: transparent;
  }
  50% {
    background-color: var(--primary-100);
  }
}

.menu-content.sel {
  max-height: 500px;
  transition: max-height 0.3s ease-in;
  overflow-y: auto;
  background-color: #fff;
  position: relative;
  padding: 0 1rem 0.5rem 1rem;
  margin: 0;
}

.highlight-selected {
  animation: highlight-pulse 0.8s cubic-bezier(0.4, 0, 0.6, 1) 1;
}

@keyframes highlight-pulse {
  0%, 100% {
    background-color: transparent;
  }
  50% {
    background-color: rgba(30, 69, 145, 0.15);
  }
}

.group:hover {
  background-color: #f8fafc;
  border-color: #94a3b8;
}

.bg-gray-50.border-gray-300.shadow-sm {
  background-color: #f1f5f9;
  border-color: #64748b;
}

.editorTopTool.bg-blue-500 {
    background-color: var(--primary-500);
    color: white;
    transition: background-color 0.3s ease;
}

.save-status-indicator {
  position: fixed;
  bottom: 12px;
  right: calc(25% + 20px);
  background-color: var(--primary-700);
  color: white;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  display: flex;
  align-items: center;
  z-index: 999;
  backdrop-filter: blur(8px);
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  border: 1px solid rgba(255,255,255,0.1);
}

@media (max-width: 1280px) {
  .save-status-indicator {
    right: calc(33.333% + 20px);
  }
}

@media (max-width: 768px) {
  .save-status-indicator {
    right: calc(33.333% + 20px);
  }
}

.save-status-indicator.is-saving {
  background-color: var(--primary-600);
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.save-status-content {
  display: flex;
  align-items: center;
  white-space: nowrap;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}

.save-status-content :deep(svg) {
  display: inline-block;
  vertical-align: middle;
}

.group.flex.items-center {
  padding: 4px 8px !important;
  min-height: 36px;
  margin-bottom: 6px;
}

#editorRightPanel table {
  width: 100%;
  table-layout: fixed;
}

#editorRightPanel table td {
  padding: 6px 0;
  font-size: 12px !important;
  line-height: 1.4;
  vertical-align: top;
}

#editorRightPanel table td:first-child {
  width: 30%;
  padding-right: 8px;
  color: #4b5563;
  font-size: 12px !important;
}

#editorRightPanel table td:last-child {
  width: 70%;
  padding-right: 12px;
  word-break: break-word;
}

#editorRightPanel table td a {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sticky-top-controls .flex.items-center.pb-1 {
  padding: 4px 0 !important;
  margin-top: 4px !important;
}

#editorRightPanel table td:first-child {
  font-weight: normal;
}

.eMfile td:first-child {
  font-size: 12px !important;
}

.eMfile td {
  font-size: 12px !important;
}

.item-selected {
  background-color: rgba(30, 69, 145, 0.08);
  border-color: rgba(30, 69, 145, 0.4) !important;
  transition: background-color 0.2s ease-in-out, border-color 0.2s ease-in-out;
}

.group.flex.items-center {
  padding: 4px 8px !important;
  min-height: 36px;
  margin-bottom: 6px;
  transition: background-color 0.2s ease-in-out, border-color 0.2s ease-in-out;
}

#canvasBox {
    touch-action: manipulation;
    overflow: hidden;
}

#canvasBox.drawing {
    -webkit-touch-callout: none !important;
    -webkit-user-select: none !important;
    -khtml-user-select: none !important;
    -moz-user-select: none !important;
    -ms-user-select: none !important;
    user-select: none !important;
}

#canvasBox.drawing * {
    -webkit-touch-callout: none !important;
    -webkit-user-select: none !important;
    -khtml-user-select: none !important;
    -moz-user-select: none !important;
    -ms-user-select: none !important;
    user-select: none !important;
}

.key-indicator {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    padding: 2px 6px;
    margin: 2px 0;
    font-size: 12px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-weight: normal;
    color: #374151;
    background-color: #f3f4f6;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.1);
    flex-shrink: 0;
}
</style>